import { motion } from 'framer-motion';
import { CheckCircle, Clock, DollarSign, Users, Award, Phone, Globe, FileText, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

const benefits = [
  'Permanent Green Card for entire family',
  'No sponsorship or job offer required',
  'Work anywhere in the USA',
  'Children get in-state tuition',
  'Path to US citizenship',
  'Investment can be returned',
  'No language requirement',
  'No business management required',
];

const investmentOptions = [
  { type: 'TEA Investment', amount: '$800,000', description: 'Targeted Employment Area - Rural or high unemployment zone', popular: true },
  { type: 'Standard Investment', amount: '$1,050,000', description: 'Non-TEA areas with job creation requirement', popular: false },
];

const processSteps = [
  { step: 1, title: 'Project Selection', duration: '2-4 weeks', description: 'Choose a vetted Regional Center project or direct investment' },
  { step: 2, title: 'Source of Funds', duration: '4-8 weeks', description: 'Document lawful source of investment capital' },
  { step: 3, title: 'I-526E Filing', duration: '18-24 months', description: 'File immigrant petition with USCIS' },
  { step: 4, title: 'Consular Processing', duration: '6-12 months', description: 'Interview at US Embassy for visa stamp' },
  { step: 5, title: 'Conditional Green Card', duration: '2 years', description: 'Receive conditional permanent residency' },
  { step: 6, title: 'I-829 Filing', duration: '12-18 months', description: 'Remove conditions and get permanent Green Card' },
];

const documents = [
  'Valid Passport',
  'Source of Funds Documentation',
  'Tax Returns (5+ years)',
  'Bank Statements',
  'Business Ownership Documents',
  'Property Sale Documentation',
  'Police Clearance',
  'Medical Examination',
];

const USAEb5Page = () => {
  usePageMeta(
    'USA EB-5 Investor Visa from India 2026 | Green Card $800K Investment — Zafco HR',
    'USA EB-5 Investor Visa consulting by Zafco HR. Permanent Green Card for family via $800,000 TEA investment. No language/sponsor required. MEA-approved overseas consultants.'
  );
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20USA%20EB-5%20visa', '_blank');
  };

  return (
    <div data-testid="usa-eb5-page">
      <PageHeader 
        title="EB-5 Investor Job Placement USA 2026"
        subtitle="Fastest path to US Green Card through the Immigrant Investor Program"
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'USA', path: '#' },
          { label: 'EB-5 Visa' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1585845597714-b4abdc6824fb?w=1920"
      />

      {/* Introduction */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                EB-5 Immigrant Investor Program
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                US Green Card Through Investment
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The EB-5 Immigrant Investor Program offers the fastest and most straightforward path to 
                US permanent residency for qualified investors. Unlike employment-based visas, EB-5 
                doesn't require a job offer or employer sponsorship.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Zafco's EB-5 specialists guide you through project selection, source of funds documentation, 
                and the entire immigration process to ensure a successful Green Card application.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Clock, label: 'Total Timeline', value: '4-5 Years' },
                  { icon: DollarSign, label: 'TEA Investment', value: '$800,000' },
                  { icon: Users, label: 'Family Included', value: 'Yes' },
                  { icon: Award, label: 'Job Creation', value: '10 Jobs' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl p-4">
                    <stat.icon className="w-6 h-6 text-zafco-teal mb-2" />
                    <p className="text-2xl font-bold text-zafco-blue">{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                ))}
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Schedule EB-5 Consultation
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free EB-5 Eligibility Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Investment Options */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              EB-5 Investment Requirements 2026
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Two investment tiers based on project location and employment area designation.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {investmentOptions.map((option, index) => (
              <motion.div
                key={option.type}
                className={`relative bg-white rounded-2xl p-8 shadow-sm ${option.popular ? 'ring-2 ring-zafco-gold' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {option.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-zafco-gold to-zafco-orange text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </span>
                )}
                <div className="text-center">
                  <Building className="w-16 h-16 text-zafco-blue mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-zafco-blue mb-2">{option.type}</h3>
                  <p className="text-4xl font-bold text-zafco-teal mb-4">{option.amount}</p>
                  <p className="text-gray-600">{option.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              EB-5 Process Timeline
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={`step-${step.step}`}
                className="flex gap-6 mb-8"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-zafco-blue to-zafco-teal flex items-center justify-center text-white font-bold">
                    {step.step}
                  </div>
                  {index < processSteps.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gradient-to-b from-zafco-blue to-zafco-teal mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-zafco-blue">{step.title}</h3>
                      <span className="text-sm text-zafco-teal font-medium">{step.duration}</span>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mb-4">
              Benefits of EB-5 Green Card
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue via-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Invest in Your American Dream?
          </motion.h2>
          <motion.p 
            className="text-xl text-white/80 mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Schedule a confidential consultation with our EB-5 specialists today.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Button 
              onClick={openWhatsApp}
              size="lg"
              className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
            >
              <Phone className="w-6 h-6 mr-3" />
              Get Free Consultation
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default USAEb5Page;
